import { apiRoutes } from '@/constants/api-routes';
import type { GoalStatus, GoalType, ReadingGoalView } from '@/types/api';
import { apiRequest } from './http';

/**
 * Body for `POST /reading-goals`. Mirrors `CreateGoalDto` on the server —
 * `totalUnits` and `totalDays` are NOT included; the server derives them
 * from the book's metadata so a malicious client can't game the plan.
 */
export interface CreateGoalPayload {
  readerId: string;
  bookId: string;
  overallGoal?: string;
  goalType: GoalType;
  perDay: number;
}

/**
 * Reading-tracker API wrapper. Mirrors `bookmarksService` — every method
 * is a thin pass-through over `apiRequest`. Response shapes are typed
 * via `ReadingGoalView` from `@/types/api`.
 *
 * `active()` returns `null` (not 404) when there's no active goal for
 * the (reader, book) pair — the wizard uses the absence to decide
 * whether to start at Screen 1 or jump to Screen 5.
 */
export const readingTrackerService = {
  list(readerId: string, status?: GoalStatus) {
    return apiRequest<ReadingGoalView[]>(apiRoutes.readingGoals.list, {
      query: { readerId, status: status ?? null },
    });
  },

  active(readerId: string, bookId: string) {
    return apiRequest<ReadingGoalView | null>(apiRoutes.readingGoals.active, {
      query: { readerId, bookId },
    });
  },

  get(id: string) {
    return apiRequest<ReadingGoalView>(apiRoutes.readingGoals.one(id));
  },

  create(payload: CreateGoalPayload) {
    return apiRequest<ReadingGoalView>(apiRoutes.readingGoals.create, {
      method: 'POST',
      body: payload,
    });
  },

  /**
   * Marks the next day complete. The server returns 409 with
   * `{ error: 'OUT_OF_ORDER', nextExpected }` if the day number doesn't
   * match the next expected slot — call sites should catch `ApiError`
   * and inspect `body.nextExpected` to phrase the toast.
   */
  markDay(id: string, dayNumber: number) {
    return apiRequest<ReadingGoalView>(apiRoutes.readingGoals.markDay(id), {
      method: 'POST',
      body: { dayNumber },
    });
  },

  abandon(id: string) {
    return apiRequest<ReadingGoalView>(apiRoutes.readingGoals.abandon(id), {
      method: 'POST',
    });
  },

  remove(id: string) {
    return apiRequest<void>(apiRoutes.readingGoals.one(id), {
      method: 'DELETE',
    });
  },
};
